export type AiCisoConversationMode = 'STANDARDS_RAG' | 'INTERNAL_AI_RAG' | 'EXTERNAL_AI_RAG'

export type ConversationModeOption = {
  mode: AiCisoConversationMode
  available: boolean
  selectable: boolean
  reason?: string | null
  providerKey?: string | null
  modelKey?: string | null
  providerDisplayName?: string | null
}

export type ConversationModeRuntime = {
  conversationMode?: AiCisoConversationMode | string | null
  preferredProviderKey?: string | null
  preferredModelKey?: string | null
  userConsentPreference?: string | null
  organizationPolicyVersion?: string | null
  modes?: ConversationModeOption[]
  defaultMode?: AiCisoConversationMode | string
  organizationAiPolicy?: Record<string, unknown> | null
  consentRequiredForExternal?: boolean
  secureEgressRequiredForExternal?: boolean
  consentSatisfied?: boolean
  egressAuthorized?: boolean
}

export type ConversationSession = {
  id: string
  title: string
  contextScope: string
  mode: string
  conversationMode?: AiCisoConversationMode | string | null
  preferredProviderKey?: string | null
  preferredModelKey?: string | null
  userConsentPreference?: string | null
  organizationPolicyVersion?: string | null
  status: string
  archivedAt: string | null
  lastInteractionAt: string
  messageCount: number
  detectedIntent?: string | null
  conversationKind?: string | null
  messagePreview?: { role: string; content: string; createdAt: string } | null
  modeRuntime?: ConversationModeRuntime | null
}

export type CitationCard = {
  sourceType?: string | null
  sourceEntityId?: string | null
  filename?: string | null
  citationLabel?: string | null
  section?: string | null
  page?: string | number | null
  excerpt?: string | null
  confidence?: number | null
  standardCode?: string | null
  controlCode?: string | null
  articleRef?: string | null
  clauseRef?: string | null
}

export type RecommendationCard = {
  id?: string
  title?: string
  description?: string
  priority?: string
  severity?: string
}

export type SourceSummaryItem = {
  primary: string
  secondary?: string
  sourceType?: string | null
  standardCode?: string | null
  temporalClass?: string | null
  freshnessNote?: string | null
}

export type ConversationMessage = {
  id: string
  role: string
  content: string
  citations: CitationCard[]
  providerKey?: string | null
  modelKey?: string | null
  providerType?: string | null
  confidenceLevel: string | null
  confidenceScore?: number | null
  insufficientContext: boolean
  createdAt: string
  topRecommendations?: RecommendationCard[]
  recommendationCount?: number
  criticalRecommendationCount?: number
  evidenceSufficient?: boolean | null
  topRisks?: Array<{ entityType?: string; entityId?: string; excerpt?: string }>
  affectedAssets?: Array<{ id?: string; name?: string }>
  affectedSystems?: Array<Record<string, unknown>>
  affectedSuppliers?: Array<Record<string, unknown>>
  affectedStandards?: string[]
  standardsCitations?: CitationCard[]
  diagnostics?: Record<string, unknown> | null
  detectedIntent?: string | null
  answerType?: string | null
  displayMode?: string | null
  sourceSummary?: SourceSummaryItem[]
  sections?: {
    executiveSummary?: string
    keyFindings?: string[]
    recommendations?: string[]
    limitations?: string[]
    actionPlan?: {
      advisoryOnly?: boolean
      modelVersion?: string
      horizons?: Array<{
        id?: string
        label?: string
        actions?: Array<{
          title?: string
          actionPriority?: string
          confidence?: string
          proposedAction?: string
          verificationCriteria?: string | string[]
          expectedQualitativeBenefit?: string
          measuredOutcome?: unknown
          ownerUnknown?: boolean
          proposedResponsibilityRole?: string | null
        }>
      }>
    } | null
    prioritizedItems?: Array<{
      rank?: number
      title?: string
      actionPriority?: string
      confidence?: string
      officialRiskLevel?: string | number | null
      urgencyNote?: string
      explanation?: {
        usedFactors?: string[]
        missingFactors?: string[]
        increasedPriority?: string[]
        reducedPriority?: string[]
        uncertainties?: string[]
      }
    }> | null
    managementSummary?: {
      executiveSummary?: string
      advisoryStatus?: string
      sections?: {
        currentPriorities?: { title?: string; items?: Array<Record<string, unknown>> }
        affectedScopes?: { title?: string; items?: Array<Record<string, unknown>> }
        actionPlanOverview?: {
          title?: string
          horizons?: Array<{ id?: string; label?: string; count?: number; sampleTitles?: string[] }>
        }
        managementApprovals?: { title?: string; items?: Array<Record<string, unknown>> }
        limitations?: { title?: string; items?: string[] }
      }
    } | null
  }
  aiEnhancedExplanation?: boolean
  responseStatus?: string | null
  executionMetadata?: {
    providerKey?: string | null
    providerDisplayName?: string | null
    model?: string | null
    executionMode?: string | null
    ragUsed?: boolean
    contextSources?: string[]
    fallbackUsed?: boolean
    fallbackReason?: string | null
    policyDecision?: Record<string, unknown> | null
    requestId?: string | null
    ownership?: string | null
  } | null
}

export type DisclosurePreview = {
  required?: boolean
  requestId?: string
  status?: string
  purpose?: string
  preview?: {
    question?: string
    facts?: Record<string, unknown>
    recommendationTitles?: string[]
    citations?: string[]
    dataNotSent?: string[]
  }
  providerKey?: string
  providerLabel?: string
  purposeLabel?: string
  modelKey?: string | null
  payloadHash?: string
  payloadRevision?: number
  expiresAt?: string | null
  productDisclosureMode?: string
  redactedCategories?: string[]
  warnings?: string[]
  editable?: {
    userPrompt?: string
    contextSummary?: string
    includeContext?: boolean
    includeDocumentRag?: boolean
    includeRegulatoryRag?: boolean
    includePlatformData?: boolean
  }
  transmitted?: {
    messages?: Array<{ role?: string; content?: string }>
    systemInstructionDisclosure?: string | null
    sections?: Record<string, string | null | undefined>
  }
  excluded?: { dataNotSent?: string[] }
  dataSent?: string[]
  dataNotSent?: string[]
}

export type PendingDisclosure = DisclosurePreview & {
  sessionId: string
  messageId: string
}

export type AiShellMode = 'closed' | 'panel' | 'workspace'

export type ProviderStatusKind =
  | 'external_available'
  | 'local_available'
  | 'approval_required'
  | 'policy_blocked'
  | 'not_configured'
  | 'temporarily_unavailable'
  | 'unknown'

export const GENERATION_PURPOSE = 'governance_organization_conversation'
export const DEFAULT_SCOPE = 'AI_CISO'

export const OPTIONAL_FILTERS = [
  { value: '', labelKey: 'filterAll' },
  { value: 'EVIDENCE', labelKey: 'filterEvidence' },
  { value: 'POLICY', labelKey: 'filterPolicies' },
  { value: 'CONTROL', labelKey: 'filterControls' },
  { value: 'STANDARD', labelKey: 'filterStandards' },
  { value: 'ASSET', labelKey: 'filterAssets' },
  { value: 'INFORMATION_SYSTEM', labelKey: 'filterInformationSystems' },
  { value: 'VULNERABILITY', labelKey: 'filterVulnerabilities' },
  { value: 'SUPPLIER', labelKey: 'filterSuppliers' },
  { value: 'AUDIT_READINESS', labelKey: 'filterAuditReadiness' },
  { value: 'INTEGRATION', labelKey: 'filterIntegrations' },
  { value: 'CERTIFICATION', labelKey: 'filterCertification' },
  { value: 'GOVERNANCE_INTELLIGENCE', labelKey: 'filterGovernanceIntelligence' },
] as const
